// Native
import ReCAPTCHA from "react-google-recaptcha"
import { useState } from "react"
import GlobeCanvas from "../../components/GlobeCanvas"

// Components
import {
    Box, Button, Divider, Flex, HStack, Image, Text, VStack,
    useColorModeValue, useToast,
} from "@chakra-ui/react"
import { Form } from "components"

// Config
import { API } from "config"

const ORANGE  = "#F7941D"
const ORANGE2 = "#E07910"
const ORANGE3 = "#CC6A0A"

const FEATURES = [
    "Controle de pedidos e transações em tempo real",
    "Gestão completa da rede de consultores",
    "Relatórios financeiros e de desempenho",
]

export default function Auth() {
    const toast    = useToast()
    const [recaptcha, setRecaptha] = useState()
    const [form,    setForm]    = useState({})
    const [errors,  setErrors]  = useState()
    const [loading, setLoading] = useState(false)

    const enabled = !!form?.email?.trim() && !!form?.password?.trim() && !!recaptcha
    const siteKey = process.env.REACT_APP_SITEKEY

    const onChange = ({ target }) => {
        const { name, value } = target
        setForm({ ...form, [name]: value })
    }

    const handleAuth = async () => {
        if (!enabled) return
        setLoading(true)
        toast({ title: "Verificando acesso...", status: "loading", duration: 1000 })
        try {
            const callback = await API.post("/employees/auth", form)
            localStorage.setItem("@token", callback.data.plainTextToken)
            toast({ title: "Seja bem vindo ao backoffice.", status: "success" })
            window.location.reload()
        } catch (err) {
            setErrors(err?.response?.data?.errors)
            toast({
                title: "Não foi possível autenticar agora.",
                description: err?.response?.data?.message,
                status: "error",
            })
            setLoading(false)
        }
    }

    const panelBg   = useColorModeValue("#FFFFFF", "#111111")
    const textMuted = useColorModeValue("gray.500", "gray.400")
    const labelC    = useColorModeValue("gray.600", "gray.300")
    const borderC   = useColorModeValue("gray.100", "#2A2A2A")

    return (
        <Flex h="100vh" overflow="hidden">

            {/* ── Left — dark brand panel ── */}
            <Flex
                flex={1}
                display={{ base: "none", lg: "flex" }}
                direction="column"
                justify="space-between"
                position="relative"
                overflow="hidden"
                style={{ background: 'linear-gradient(155deg, #070c17 0%, #0d1525 50%, #0a1628 100%)' }}
                p={12}
            >
                {/* Globe — centralizado com leve deslocamento à direita */}
                <Box
                    position="absolute"
                    top={0} left={0} right={0} bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{ pointerEvents: 'none' }}
                    zIndex={0}
                >
                    <GlobeCanvas size={720} />
                </Box>

                {/* Gradient top */}
                <Box
                    position="absolute"
                    top={0} left={0} right={0} h="42%"
                    style={{ background: 'linear-gradient(to bottom, rgba(7,12,23,0.93) 0%, rgba(7,12,23,0.52) 68%, transparent 100%)', pointerEvents: 'none' }}
                    zIndex={1}
                />

                {/* Gradient bottom */}
                <Box
                    position="absolute"
                    bottom={0} left={0} right={0} h="42%"
                    style={{ background: 'linear-gradient(to top, rgba(10,22,40,0.93) 0%, rgba(10,22,40,0.52) 68%, transparent 100%)', pointerEvents: 'none' }}
                    zIndex={1}
                />

                {/* Top: brand badge — mesmo padrão do consultor */}
                <HStack spacing={3} zIndex={2}>
                    <Box
                        p="8px"
                        borderRadius="12px"
                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                        <Image src="/img/logo.png" h="30px" w="30px" objectFit="contain" />
                    </Box>
                    <Box>
                        <Text color="white" fontWeight="bold" fontSize="md" lineHeight="1">Bonífica</Text>
                        <Box mt="4px" display="inline-flex">
                            <Box
                                px={2} py="2px"
                                borderRadius="full"
                                style={{ background: 'rgba(251,146,60,0.18)', border: '1px solid rgba(251,146,60,0.32)' }}
                            >
                                <Text color="orange.300" fontSize="xs" fontWeight="medium">Backoffice Administrativo</Text>
                            </Box>
                        </Box>
                    </Box>
                </HStack>

                {/* Bottom: headline + stats + features */}
                <VStack align="flex-start" spacing={5} zIndex={2}>
                    <Box>
                        <Text
                            color="white"
                            fontSize={{ lg: "2xl", xl: "3xl" }}
                            fontWeight="bold"
                            lineHeight="1.3"
                            letterSpacing="-0.02em"
                        >
                            Gerencie a plataforma<br />com total controle
                        </Text>
                        <Text mt={2} fontSize="sm" style={{ color: 'rgba(255,255,255,0.50)' }} lineHeight="1.6">
                            Controle centralizado de pedidos, consultores, finanças e muito mais.
                        </Text>
                    </Box>

                    {/* Stats */}
                    <HStack
                        spacing={0}
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                        borderRadius="xl"
                        p={4}
                        divide={<Divider orientation="vertical" h="28px" borderColor="rgba(255,255,255,0.08)" mx={4} />}
                    >
                        {[
                            { value: "120k+", label: "Consultores"  },
                            { value: "1M+",   label: "Produtos"     },
                            { value: "5 ★",   label: "Avaliação"    },
                        ].map(({ value, label }) => (
                            <Box key={label} textAlign="center" flex={1}>
                                <Text color={ORANGE} fontSize="md" fontWeight="extrabold" lineHeight={1}>{value}</Text>
                                <Text style={{ color: 'rgba(255,255,255,0.4)' }} fontSize="2xs" textTransform="uppercase" letterSpacing="wider" mt={1}>{label}</Text>
                            </Box>
                        ))}
                    </HStack>

                    {/* Feature highlights */}
                    <VStack align="flex-start" spacing={2}>
                        {FEATURES.map(f => (
                            <HStack key={f} spacing={2}>
                                <Box w="5px" h="5px" borderRadius="full" bg={ORANGE} flexShrink={0} />
                                <Text style={{ color: 'rgba(255,255,255,0.50)' }} fontSize="xs">{f}</Text>
                            </HStack>
                        ))}
                    </VStack>
                </VStack>
            </Flex>

            {/* ── Right — form panel ── */}
            <Flex
                w={{ base: "100%", lg: "480px" }}
                flexShrink={0}
                bg={panelBg}
                direction="column"
                justify="center"
                align="center"
                px={{ base: 8, md: 14 }}
                py={12}
                borderLeftWidth="1px"
                borderLeftColor={borderC}
                position="relative"
            >
                <Box
                    position="absolute"
                    top={0} left={0} right={0}
                    h="3px"
                    style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(251,146,60,0.7) 50%, transparent 100%)' }}
                />

                <Box mb={8} w="100%">
                    <Text fontSize="2xl" fontWeight="bold" letterSpacing="-0.02em" lineHeight={1.25}>Bem-vindo(a) de volta</Text>
                    <Text fontSize="sm" color={textMuted} mt={1}>Acesso restrito a colaboradores autorizados</Text>
                </Box>

                <Form.Form onSubmit={handleAuth} spacing={6} w="100%">
                    <Form.Text
                        required
                        type="email"
                        label="Endereço de e-mail"
                        placeholder="colaborador@bonificaoficial.com"
                        name="email"
                        value={form?.email}
                        error={errors?.email}
                        onChange={onChange}
                        isDisabled={loading}
                        focusBorderColor={ORANGE}
                        size="lg"
                    />

                    <Form.Password
                        required
                        label="Senha"
                        placeholder="••••••••"
                        name="password"
                        value={form?.password}
                        error={errors?.password}
                        onChange={onChange}
                        isDisabled={loading}
                        focusBorderColor={ORANGE}
                        size="lg"
                    />

                    <Box>
                        <ReCAPTCHA sitekey={siteKey} onChange={setRecaptha} />
                    </Box>

                    <Button
                        type="submit"
                        isLoading={loading}
                        isDisabled={!enabled}
                        w="100%"
                        size="lg"
                        bg={ORANGE}
                        color="white"
                        fontWeight="bold"
                        borderRadius="xl"
                        fontSize="sm"
                        letterSpacing="wide"
                        _hover={{ bg: ORANGE2, transform: "translateY(-2px)", boxShadow: `0 10px 32px rgba(247,148,29,0.45)` }}
                        _active={{ bg: ORANGE3, transform: "translateY(0)", boxShadow: "none" }}
                        _disabled={{ opacity: 0.45, transform: "none", boxShadow: "none" }}
                        transition="all 0.2s"
                        mt={1}
                    >
                        Entrar no sistema
                    </Button>
                </Form.Form>

                <Box mt={8} textAlign="center" borderTopWidth="1px" borderColor={borderC} pt={6} w="100%">
                    <Text fontSize="sm" color={textMuted}>Não consegue acessar? Fale com o Suporte de T.I</Text>
                </Box>
            </Flex>

        </Flex>
    )
}
